import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import {
  SELECTABLE_OUTCOMES,
  addDays,
  ruleVersionOn,
  scoreDay,
  type Operation,
  type RepeatRule,
  type SelectableOutcome,
} from '../../packages/domain/src/index'
import { entryFor, useOwnerMatrix } from '../owner/OwnerMatrixContext'
import { useOffline } from '../offline'

const outcomeLabels: Record<SelectableOutcome, string> = { T: 'Tick', W: 'Winning', C: 'Adjust', B: 'Bye' }

function formatDate(date: string): string {
  return new Intl.DateTimeFormat('en-AU', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(`${date}T00:00:00Z`))
}

export function scheduleLabel(rule: RepeatRule): string {
  if (rule.period.kind === 'rolling' && rule.period.days === 1 && rule.quota === 1 && rule.weekdays === null) return 'Daily'
  const period = rule.period.kind === 'rolling' ? `rolling ${rule.period.days} days` : rule.period.kind
  const weekdays = rule.weekdays ? ` · days ${rule.weekdays.join(', ')}` : ''
  return `${rule.quota}× per ${period}${weekdays}`
}

export function TodayScreen() {
  const { document, trustedToday, mode, setOutcome } = useOwnerMatrix()
  const [selectedDate, setSelectedDate] = useState(trustedToday)
  const previousTrustedToday = useRef(trustedToday)
  const [remainingOnly, setRemainingOnly] = useState(false)
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
  const [outcomeMenu, setOutcomeMenu] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const triggerRefs = useRef(new Map<string, HTMLButtonElement>())
  const { recordEntry } = useOffline()
  const day = useMemo(() => scoreDay(document, selectedDate), [document, selectedDate])
  const contributionByOperation = useMemo(() => new Map(day.contributions.map((contribution) => [contribution.operationId, contribution])), [day])
  const recordedDue = day.contributions.filter((contribution) => contribution.due && contribution.recorded).length
  const completion = day.dueCount ? Math.round(recordedDue / day.dueCount * 100) : null

  useEffect(() => {
    if (outcomeMenu) menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus()
  }, [outcomeMenu])

  useEffect(() => {
    const previous = previousTrustedToday.current
    previousTrustedToday.current = trustedToday
    setSelectedDate((current) => current === previous ? trustedToday : current)
  }, [trustedToday])

  const closeOutcomeMenu = (returnFocus = true) => {
    const operationId = outcomeMenu
    setOutcomeMenu(null)
    if (returnFocus && operationId) requestAnimationFrame(() => triggerRefs.current.get(operationId)?.focus())
  }

  const handleMenuKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')]
    const index = items.indexOf(globalThis.document.activeElement as HTMLButtonElement)
    let next = index
    if (event.key === 'ArrowDown') next = (index + 1) % items.length
    else if (event.key === 'ArrowUp') next = (index - 1 + items.length) % items.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = items.length - 1
    else if (event.key === 'Escape') { event.preventDefault(); closeOutcomeMenu(); return }
    else return
    event.preventDefault()
    items[next]?.focus()
  }

  const toggleCategory = (id: string) => setCollapsed((current) => {
    const next = new Set(current)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  const chooseOutcome = (operationId: string, outcome: SelectableOutcome | null) => {
    setOutcome(operationId, selectedDate, outcome)
    const sourceKey = document.operations.find((operation) => operation.id === operationId)?.sourceKey ?? operationId
    void recordEntry({ ownerId: document.ownerId, operationId: sourceKey, date: selectedDate, outcome, deleted: outcome === null })
    closeOutcomeMenu()
  }

  return (
    <div className="screen today-screen">
      <ScreenHeading eyebrow={selectedDate === trustedToday ? 'Today' : 'Backfill'} title={formatDate(selectedDate)} description={`${mode === 'remote-owner' ? 'Authenticated owner' : 'Synthetic staging preview'} · Brisbane day · browser score uses the shared domain engine`}>
        <div className="date-controls" aria-label="Choose date">
          <button type="button" className="icon-button" aria-label="Previous day" onClick={() => setSelectedDate(addDays(selectedDate, -1))}>‹</button>
          <label className="date-picker"><span className="sr-only">Entry date</span><input type="date" max={trustedToday} value={selectedDate} onChange={(event) => event.target.value && setSelectedDate(event.target.value)} /></label>
          <button type="button" className="button" aria-current={selectedDate === trustedToday ? 'date' : undefined} onClick={() => setSelectedDate(trustedToday)}>Today</button>
          <button type="button" className="icon-button" aria-label="Next day" disabled={selectedDate >= trustedToday} onClick={() => setSelectedDate(addDays(selectedDate, 1))}>›</button>
        </div>
      </ScreenHeading>

      <div className="today-layout">
        <aside className="category-rail panel" aria-label="Categories">
          <p className="overline">Categories</p>
          {document.categories.map((category) => {
            const contributions = day.contributions.filter((item) => item.categoryId === category.id)
            return <a key={category.id} href={`#category-${category.id}`}><span>{category.name}</span><small>{contributions.filter((item) => item.recorded && item.due).length}/{contributions.filter((item) => item.due).length}</small></a>
          })}
        </aside>

        <div className="worklist">
          <div className="worklist-toolbar panel">
            <div><strong>Operations</strong><p>Optional work stays out of Remaining until the shared due engine marks it due.</p></div>
            <div className="segmented" aria-label="Filter operations">
              <button type="button" aria-pressed={!remainingOnly} onClick={() => setRemainingOnly(false)}>All</button>
              <button type="button" aria-pressed={remainingOnly} onClick={() => setRemainingOnly(true)}>Remaining ({day.remainingOperationIds.length})</button>
            </div>
          </div>

          {document.categories.map((category) => {
            const categoryDay = scoreDay(document, selectedDate, { categoryId: category.id })
            const allOperations = category.operationOrder.flatMap((id) => {
              const operation = document.operations.find((candidate) => candidate.id === id)
              return operation ? [operation] : []
            })
            const operations = remainingOnly ? allOperations.filter((operation) => categoryDay.remainingOperationIds.includes(operation.id)) : allOperations
            const expanded = !collapsed.has(category.id)
            return (
              <section className="operation-group panel" id={`category-${category.id}`} key={category.id}>
                <button type="button" className="category-heading" aria-expanded={expanded} onClick={() => toggleCategory(category.id)}>
                  <span className="chevron" aria-hidden="true">{expanded ? '▼' : '▶'}</span>
                  <span><strong>{category.name}</strong><small>{operations.length} shown</small></span>
                  <span className="category-score"><small>Score</small><strong>{categoryDay.score === null ? '—' : Math.round(categoryDay.score)}</strong></span>
                </button>
                {expanded && <div className="operation-list">
                  {operations.length ? operations.map((operation) => {
                    const contribution = contributionByOperation.get(operation.id)
                    const entry = entryFor(document, operation.id, selectedDate)
                    const version = contribution?.ruleVersionId ? ruleVersionOn(operation.ruleVersions, selectedDate) : null
                    const unavailable = contribution?.eligible !== true
                    return (
                      <article className="operation-row" key={operation.id}>
                        <div className="operation-name">
                          <strong>{operation.name}{operation.private && <span className="privacy-chip">Private</span>}</strong>
                          <small>{version ? scheduleLabel(version.rule) : 'Inactive'} · {unavailable ? 'Not scheduled' : contribution?.due ? 'Due' : 'Optional — excluded from Remaining'}</small>
                        </div>
                        <button type="button" className={`tick-button ${entry?.outcome === 'T' ? 'selected' : ''}`} aria-pressed={entry?.outcome === 'T'} disabled={unavailable} onClick={() => chooseOutcome(operation.id, entry?.outcome === 'T' ? null : 'T')}>
                          {entry?.outcome === 'T' ? 'Ticked' : 'Tick'}
                        </button>
                        <div className="outcome-menu-wrap">
                          <button ref={(node) => { if (node) triggerRefs.current.set(operation.id, node); else triggerRefs.current.delete(operation.id) }} type="button" className="button outcome-trigger" disabled={unavailable} aria-haspopup="menu" aria-expanded={outcomeMenu === operation.id} onKeyDown={(event) => { if (event.key === 'ArrowDown') { event.preventDefault(); setOutcomeMenu(operation.id) } else if (event.key === 'Escape' && outcomeMenu === operation.id) closeOutcomeMenu() }} onClick={() => outcomeMenu === operation.id ? closeOutcomeMenu(false) : setOutcomeMenu(operation.id)}>{entry?.outcome ?? 'More…'}</button>
                          {outcomeMenu === operation.id && <div ref={menuRef} className="outcome-popover panel" role="menu" aria-label={`Outcome for ${operation.name}`} onKeyDown={handleMenuKey}>
                            {SELECTABLE_OUTCOMES.map((code) => <button role="menuitem" type="button" key={code} onClick={() => chooseOutcome(operation.id, code)}><b data-code={code}>{code}</b><span>{outcomeLabels[code]}</span></button>)}
                            <button role="menuitem" type="button" onClick={() => chooseOutcome(operation.id, null)}>Clear</button>
                          </div>}
                        </div>
                      </article>
                    )
                  }) : <p className="empty-row">Everything due in this category is recorded.</p>}
                </div>}
              </section>
            )
          })}
        </div>

        <aside className="score-card panel" aria-label="Day progress">
          <p className="overline">Day score</p>
          <div className="hero-score">{day.score === null ? '—' : Math.round(day.score)}</div>
          <p>{completion ?? '—'} completion · {recordedDue} of {day.dueCount} due recorded</p>
          <progress className="progress-track" value={day.score ?? 0} max="100" aria-label={day.score === null ? 'No score' : `Score ${Math.round(day.score)} out of 100`} />
          <div className="metric-pair"><div><strong>{day.remainingOperationIds.length}</strong><small>Remaining</small></div><div><strong>{day.targetMet === null ? '—' : day.targetMet ? 'Yes' : 'Not yet'}</strong><small>On target</small></div></div>
          <p className="score-note">Blank optional operations leave the denominator, completion and Remaining counts.</p>
          <div className="outcome-legend"><p className="overline">Selectable outcomes</p>{SELECTABLE_OUTCOMES.map((code) => <span key={code}><b data-code={code}>{code}</b>{outcomeLabels[code]}</span>)}<small>M is displayed only for imported history.</small></div>
        </aside>
      </div>
    </div>
  )
}

export function ScreenHeading({ eyebrow, title, description, children }: { eyebrow: string; title: string; description: string; children?: React.ReactNode }) {
  return <div className="screen-heading"><div><span className="overline">{eyebrow}</span><h1>{title}</h1><p>{description}</p></div>{children}</div>
}

export function currentRule(operation: Operation, date: string): RepeatRule | null {
  try { return ruleVersionOn(operation.ruleVersions, date).rule } catch { return null }
}
